import React, { useRef, useCallback } from 'react';
import skull from '../../../content/killIndicators/icon_skull_default.svg';
import c4 from '../../../content/weapons/c4.svg';
import bombDefused from '../../../content/roundEnds/bomb_defused.svg';
import bombExploded from '../../../content/roundEnds/bomb_exploded.svg';
import smokegrenade from '../../../content/weapons/smokegrenade.svg';

export interface TimelineEvent {
    frameIdx: number;
    type: 'kill' | 'bomb_plant' | 'bomb_defused' | 'bomb_exploded' | 'smoke_break';
    team?: 'T' | 'CT';
}

interface ReplayTimelineProps {
    currentFrame: number;
    totalFrames: number;
    onSeek: (frame: number) => void;
    events: TimelineEvent[];
    isBombPlanted: boolean;
}

const T_COLOR = '#de9b35';
const CT_COLOR = '#5d79ae';
const ACCENT = 'var(--accent-orange, #f15c25)';
const TRACK_HEIGHT = 6;
const PLAYHEAD_SIZE = 14;
const MARKER_SIZE = 11;

const ReplayTimeline: React.FC<ReplayTimelineProps> = ({
    currentFrame, totalFrames, onSeek, events, isBombPlanted,
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);

    const seekFromEvent = useCallback((clientX: number) => {
        const track = trackRef.current;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        onSeek(Math.round(ratio * Math.max(totalFrames - 1, 0)));
    }, [onSeek, totalFrames]);

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        e.preventDefault();
        window.getSelection()?.removeAllRanges();
        isDragging.current = true;
        seekFromEvent(e.clientX);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }, [seekFromEvent]);

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
        if (!isDragging.current) return;
        seekFromEvent(e.clientX);
    }, [seekFromEvent]);

    const handlePointerUp = useCallback(() => {
        isDragging.current = false;
    }, []);

    const progress = totalFrames > 1 ? (currentFrame / (totalFrames - 1)) * 100 : 0;

    const getMarkerStyle = (evt: TimelineEvent): React.CSSProperties => {
        let icon: string;
        let color: string;
        switch (evt.type) {
            case 'kill':
                icon = skull;
                color = evt.team === 'CT' ? CT_COLOR : T_COLOR;
                break;
            case 'bomb_plant':
                icon = c4;
                color = '#f0a030';
                break;
            case 'bomb_defused':
                icon = bombDefused;
                color = '#4caf50';
                break;
            case 'bomb_exploded':
                icon = bombExploded;
                color = '#e53935';
                break;
            case 'smoke_break':
                icon = smokegrenade;
                color = '#ffaa3c'; // matches the in-canvas shockwave hue
                break;
            default:
                return { width: MARKER_SIZE, height: MARKER_SIZE, borderRadius: '50%', backgroundColor: '#888' };
        }
        return {
            width: MARKER_SIZE,
            height: MARKER_SIZE,
            backgroundColor: color,
            WebkitMaskImage: `url(${icon})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url(${icon})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
        };
    };

    return (
        <div
            ref={trackRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
                position: 'relative',
                height: PLAYHEAD_SIZE + 4,
                cursor: 'pointer',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                touchAction: 'none',
                userSelect: 'none',
            }}
        >
            {/* Track background */}
            <div style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: TRACK_HEIGHT,
                backgroundColor: '#333',
                borderRadius: TRACK_HEIGHT / 2,
            }} />

            {/* Fill bar */}
            <div style={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: TRACK_HEIGHT,
                width: `${progress}%`,
                backgroundColor: isBombPlanted ? '#d4602a' : ACCENT,
                borderRadius: TRACK_HEIGHT / 2,
                transition: isDragging.current ? 'none' : undefined,
            }} />

            {/* Event markers */}
            {events.map((evt, i) => {
                const pos = totalFrames > 1
                    ? (evt.frameIdx / (totalFrames - 1)) * 100
                    : 0;
                return (
                    <div
                        key={`${evt.type}-${i}`}
                        style={{
                            position: 'absolute',
                            left: `${pos}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            ...getMarkerStyle(evt),
                            opacity: 0.9,
                            pointerEvents: 'none',
                            zIndex: 2,
                        }}
                    />
                );
            })}

            {/* Playhead */}
            <div style={{
                position: 'absolute',
                left: `${progress}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: PLAYHEAD_SIZE,
                height: PLAYHEAD_SIZE,
                borderRadius: '50%',
                backgroundColor: isBombPlanted ? '#f0a030' : ACCENT,
                border: '2px solid #fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                zIndex: 3,
                pointerEvents: 'none',
            }} />
        </div>
    );
};

export default ReplayTimeline;
